import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ClockService,
    Complaint,
    ComplaintData,
    ComplaintPosition,
    ComplaintsDocumentType,
    ComplaintsDocumentTypeDict,
    ComplaintsDocumentTypes,
    DocumentAttachment,
} from '@icc/b2b/shared';
import { ConfigService, TranslateService, core } from '@icc/shared';
import { ToastService } from '@icc/shared/helpers';
import { AuthorizedRetailer, ComplaintParty } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ComplaintService } from '../complaint.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReplaySubject, Subscription } from 'rxjs';
import { LoaderService, SyncService } from '@icc/b2b/sales-document';
import { Store } from '@ngrx/store';
import { Loader } from '@googlemaps/js-api-loader';
import { MapGeocoder } from '@angular/google-maps';
import { ComplaintPositionEditComponent } from '../complaint-position-edit/complaint-position-edit.component';
import { DialogService } from '@icc/ui';
import { ComplaintMapDialogComponent, ComplaintMapDialogData } from '../complaint-map-dialog/complaint-map-dialog.component';
import { ComplaintPartialMatchConfirmDialogComponent } from '../complaint-partial-match-confirm-dialog/complaint-partial-match-confirm-dialog.component';

@Component({
    selector: 'icc-complaint-b2c',
    templateUrl: './complaint-b2c.component.html',
    styleUrls: ['./complaint-b2c.component.scss'],
})
export class ComplaintB2cComponent implements OnInit, OnDestroy {
    @ViewChildren(ComplaintPositionEditComponent) positionEditors!: QueryList<ComplaintPositionEditComponent>;
    static crumbsSubject = new ReplaySubject<Record<string, any>>();

    data: ComplaintData = {} as ComplaintData;
    documentTypesDict = ComplaintsDocumentTypeDict;
    availableDocumentTypesDict: Record<number, string> = {};
    authorizedRetailerCompanyId: number | null = null;
    complaint: Complaint = {
        uuid: 'c-' + core.generateUUID(),
        type: 'complaint',
        version: '',
        lang: 'pl',
        clientName: '',
        installationAddress: '',
        locationDescription: undefined,
        locationSource: undefined,
        mapUnavailable: false,
        isLocationPartialMatch: false,
        locationPartialMatchConfirmed: false,
        clientEmail: '',
        clientPhoneNumber: '',
        purchaseDate: null,
        assignedTo: 'manufacturer',
        authorizedRetailerCompanyId: null,
        authorizedRetailerUserId: null,
        authorizedRetailerUserName: '',
        authorizedRetailerPriority: '350',
        distributorPriority: '350',
        manufacturerPriority: '350',
        consents: [],
        authorizedRetailer: {
            userId: null,
            companyId: null,
            referenceName: '',
            companyName: '',
            email: '',
            phoneNumber: '',
            address: '',
            companyVatNr: '',
            companyVatNrEu: '',
        },
        multipleWorkflows: false,
        documentTypeId: ComplaintsDocumentType.Complaint,
        statusChangedAt: new Date(),
        assignChangedAt: new Date(),
        noteAddedAt: new Date(),
        modifiedForComplaintTransfer: new Date(),
        statusChangeInactivitySent: false,
        assignChangeInactivitySent: false,
        noteAddedInactivitySent: false,
        documentName: null,
    };

    complaintPositions: ComplaintPosition[] = [];
    complaintPositionsAttachments: DocumentAttachment[] = [];

    noSalesPoint = false;
    formSubmitted = false;
    addressValidated = false;

    authorizedRetailers: AuthorizedRetailer[] = [];

    installationAddress: google.maps.LatLngLiteral | null = null;
    markerOptions: google.maps.MarkerOptions = { draggable: true };

    addressMode: 'exact' | 'map' = 'exact';
    mapLoadError = false;

    addressLine1: string = '';
    postalCode: string = '';
    city: string = '';

    search = '';

    complaintFormValidatedFields: FormGroup;
    showSyncedInfo = false;

    saving = false;

    apiLoaded = false;

    mapZoom: number = 15;

    documentNumber = '';
    documentTypes = ComplaintsDocumentTypes;
    Object = Object;
    number = Number;

    private subscriptions: Subscription[] = [];
    private autocompleteService: google.maps.places.AutocompleteService | null = null;
    private trustedHtmlCache = new Map<string, SafeHtml>();

    currentStep = 1;

    noAuthorizedRetailerOption = {
        userId: null,
        companyId: -1,
        referenceName: '',
        companyName: this.translateService.instant('COMPLAINT|Brak mojego punktu sprzedaży na poniższej liście'),
        address: '',
        email: '',
        phoneNumber: '',
        companyVatNr: '',
        companyVatNrEu: '',
        distributor: {
            userId: null,
            companyId: null,
            referenceName: '',
            companyName: '',
            address: '',
            email: '',
            phoneNumber: '',
            companyVatNr: '',
            companyVatNrEu: '',
        }
    };

    allConsentsChecked = false;

    positionsTitles = new Map<string, string[]>();
    positionsAttachmentsMap = new Map<string, DocumentAttachment[]>();

    activeUserType: string;

    formSteps = [
        this.translateService.instant('COMPLAINT|Zgłaszający'),
        this.translateService.instant('COMPLAINT|Dane zakupu'),
        this.translateService.instant('COMPLAINT|Dane usterek'),
        this.translateService.instant('INTERFACE|Podsumowanie'),
    ];

    isB2bComplaintForm = false;

    today = new Date().toISOString().slice(0, 10);

    constructor(
        private complaintService: ComplaintService,
        private sanitizer: DomSanitizer,
        private http: HttpClient,
        private toastService: ToastService,
        private translateService: TranslateService,
        private router: Router,
        private fb: FormBuilder,
        private syncService: SyncService,
        private store: Store,
        private activatedRoute: ActivatedRoute,
        private geocoder: MapGeocoder,
        private chRef: ChangeDetectorRef,
        private el: ElementRef,
        private loaderService: LoaderService,
        private configService: ConfigService,
        private dialogService: DialogService,
    ) {
        this.activeUserType = this.complaintService.getUserType();
        this.complaintFormValidatedFields = this.fb.group({
            clientEmail: ['', Validators.required],
            clientPhoneNumber: ['', Validators.required],
            addressLine1: [''],
            city: [''],
            postalCode: [''],
        });
    }

    async ngOnInit() {

        this.subscriptions.push(
            this.activatedRoute.data.subscribe(data => {
                this.isB2bComplaintForm = data['isB2bComplaintForm'] || false;
                if (this.isB2bComplaintForm) {
                    this.formSteps.shift();
                    this.currentStep = 2;
                    this.setCrumbs();
                }
            })
        );

        await this.loadData();
        this.complaint.lang = this.configService.config.languageCode;
        if (!this.isB2bComplaintForm) {
            this.addConsents();
        }
        this.setManufacturer();
        this.setAuthorizedRetailerFromQueryParams();
        this.setPositionFromQueryParams();
        this.addComplaintPosition(true);
        if (this.isB2bComplaintForm) {
            this.complaintService.loadContractors();
            await this.complaintService.contractorsLoaded;
            this.setCurrentUserAsReporter();
        }

        (window as any).gm_authFailure = () => {
            console.error('Google Maps API authentication failed');
            this.handleMapLoadError('AUTH_FAILURE');
        };

        const loader = new Loader({
            apiKey: this.data.googleMapsApiKey,
            version: 'weekly',
            libraries: ['places'],
        });

        const loadTimeout = setTimeout(() => {
            if (!this.apiLoaded) {
                console.error('Google Maps API load timeout');
                this.handleMapLoadError('TIMEOUT');
            }
        }, 10000);

        loader.load().then(() => {
            clearTimeout(loadTimeout);
            this.apiLoaded = true;
            this.autocompleteService = new google.maps.places.AutocompleteService();
            this.chRef.detectChanges();
        }).catch((error) => {
            clearTimeout(loadTimeout);
            console.error('Google Maps API load error:', error);
            if (error.message?.includes('quota') || error.message?.includes('OVER_QUERY_LIMIT')) {
                this.handleMapLoadError('QUOTA_EXCEEDED');
            } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
                this.handleMapLoadError('NETWORK_ERROR');
            } else {
                this.handleMapLoadError('UNKNOWN_ERROR');
            }
        });
        await this.complaintService.workflowsLoaded;
        this.availableDocumentTypesDict = this.getAvailableDocumentTypes();
        this.complaint.statusId = this.complaintService.statuses.find(el => el.documentTypeId === this.complaint.documentTypeId)?.id ?? -1;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    async loadData() {
        await this.complaintService.loaded;
        this.data = this.complaintService.data;
    }

    setCrumbs() {
        const crumbs = [];

        crumbs.push({
            label: this.translateService.instant('COMPLAINT|Zgłoszenie wewnętrzne'),
            url: '/trade/complaint-form-internal',
        });

        ComplaintB2cComponent.crumbsSubject.next({
            level: 0,
            crumbs: null,
        });

        ComplaintB2cComponent.crumbsSubject.next({
            level: 1,
            crumbs,
        });
    }

    addConsents() {
        this.complaint.consents = this.data.consents.map((consent) => ({
            id: consent.id,
            name: consent.name,
            shortDescription: consent.shortDescription,
            longDescription: consent.longDescription,
            checked: false,
            required: consent.required,
            showLongDescription: false,
        }));
    }

    setAuthorizedRetailerFromQueryParams() {
        const authorizedRetailerExtId = this.router.parseUrl(this.router.url).queryParams['authorizedRetailerId'];
        const authorizedRetailer = this.authorizedRetailers.find(
            (ar) => ar.extId === authorizedRetailerExtId
        );
        if (authorizedRetailer) {
            this.onAuthorizedRetailerChange(authorizedRetailer);
        }
    }

    setPositionFromQueryParams() {
        const queryParams = this.router.parseUrl(this.router.url).queryParams;
        if (queryParams['orderId']) {
           this.addComplaintPosition(false);
           this.complaintPositions[0].distributorOrderNumber = queryParams['orderId'];
           this.complaintPositions[0].distributorOrderPositionNumber = queryParams['positionNo'];
        }
    }

    addComplaintPosition(newProduct = false) {
        if (newProduct || this.complaintPositions.length < 1) {
            this.complaintPositions.push({
                uuid: core.generateUUID(),
                documentId: this.complaint.uuid,
                type: 'defect',
                complaintProductTypeId: 0,
                complaintProductType: '',
                complaintDocumentType: null,
                serialNumber: '',
                invalidSerialNumber: this.isB2bComplaintForm ? true : false,
                orderNumber: '',
                orderPositionNumber: '',
                defectDate: null,
                created: new Date(),
                modified: new Date(),
                quantity: 1,
                machineId: ClockService.getClock()?.timestamp.node(),
                configuration: {},
                complaintCategoryId: 0,
                defectMaintenance: null,
                toVerify: false,
                index: this.complaintPositions.length + 1,
            });
        } else {
            this.complaintPositions.push({
                uuid: core.generateUUID(),
                documentId: this.complaint.uuid,
                type: 'defect',
                complaintProductTypeId:
                    this.complaintPositions[this.complaintPositions.length - 1]
                        .complaintProductTypeId,
                complaintProductType:
                    this.complaintPositions[this.complaintPositions.length - 1]
                        .complaintProductType,
                complaintDocumentType:
                    this.complaintPositions[this.complaintPositions.length - 1]
                        .complaintDocumentType,
                serialNumber:
                    this.complaintPositions[this.complaintPositions.length - 1].serialNumber,
                orderNumber:
                    this.complaintPositions[this.complaintPositions.length - 1].orderNumber,
                orderPositionNumber: this.complaintPositions[this.complaintPositions.length - 1].orderPositionNumber,
                invoiceNumber:
                    this.complaintPositions[this.complaintPositions.length - 1].invoiceNumber,
                defectDate: null,
                created: new Date(),
                modified: new Date(),
                quantity: 1,
                machineId: ClockService.getClock()?.timestamp.node(),
                configuration: {},
                complaintCategoryId:
                    this.complaintPositions[this.complaintPositions.length - 1]
                        .complaintCategoryId,
                defectMaintenance: null,
                toVerify: false,
                index: this.complaintPositions.length + 1,
            });
        }

        setTimeout(() => {
            this.positionEditors.forEach((editor, index) => {
                if (index !== this.complaintPositions.length - 1) {
                    editor.showDetails = false;
                }
            });
        });
    }

    removeComplaintPosition(index: number) {
        this.complaintPositions.splice(index, 1);
    }

    addAttachmentToPosition(attachment: DocumentAttachment) {
        this.complaintPositionsAttachments.push(attachment);
    }

    removeAttachmentToPosition(uuid: string) {
        this.complaintPositionsAttachments = this.complaintPositionsAttachments.filter(
            (el) => el.uuid !== uuid
        );
    }

    // eslint-disable-next-line max-statements
    onAuthorizedRetailerChange(event: ComplaintParty) {
        const authorizedRetailer = this.authorizedRetailers.find(
            (ar) => ar.companyId === Number(event?.companyId)
        );
        if (authorizedRetailer && authorizedRetailer.companyId !== -1) {
            // Handle distributor data based on type
            if (authorizedRetailer.type === 'distributor') {
                // For distributors, they ARE the distributor, so set distributor data from their own info
                this.complaint.authorizedRetailerUserId = null;
                this.complaint.authorizedRetailerUserName = '';
                this.complaint.authorizedRetailerCompanyId = null;
                this.complaint.authorizedRetailerPhoneNumber = '';
                this.complaint.authorizedRetailerEmail = '';
                this.complaint.authorizedRetailer = {
                    userId: null,
                    companyId: null,
                    referenceName: '',
                    companyName: '',
                    email: '',
                    phoneNumber: '',
                    address: '',
                    companyVatNr: '',
                    companyVatNrEu: '',
                };
                this.complaint.assignedUserId = authorizedRetailer?.userId ?? null;
                this.complaint.assignedTo = 'distributor';
                this.complaint.assignedCompanyId = authorizedRetailer?.companyId ?? null;
                this.complaint.distributor = {
                    userId: authorizedRetailer.userId,
                    companyId: authorizedRetailer.companyId,
                    referenceName: authorizedRetailer.referenceName,
                    companyName: authorizedRetailer.companyName,
                    email: authorizedRetailer.email,
                    phoneNumber: authorizedRetailer.phoneNumber,
                    address: authorizedRetailer.address,
                    companyVatNr: authorizedRetailer.companyVatNr,
                    companyVatNrEu: authorizedRetailer.companyVatNrEu,
                };
                this.complaint.distributorCompanyId = authorizedRetailer.companyId;
                this.complaint.distributorUserId = authorizedRetailer.userId;
                this.complaint.distributorUserName = authorizedRetailer.referenceName;
                this.complaint.distributorPhoneNumber = authorizedRetailer.phoneNumber;
                this.complaint.distributorEmail = authorizedRetailer.email;
            } else {
                // For APS, use the distributor data from the API response
                this.complaint.authorizedRetailerUserId = authorizedRetailer?.userId;
                this.complaint.authorizedRetailerUserName = authorizedRetailer?.referenceName;
                this.complaint.authorizedRetailerCompanyId = authorizedRetailer?.companyId;
                this.complaint.authorizedRetailerPhoneNumber = authorizedRetailer?.phoneNumber;
                this.complaint.authorizedRetailerEmail = authorizedRetailer?.email;
                this.complaint.authorizedRetailer.referenceName = authorizedRetailer?.referenceName;
                this.complaint.authorizedRetailer.companyName = authorizedRetailer?.companyName;
                this.complaint.authorizedRetailer.email = authorizedRetailer?.email;
                this.complaint.authorizedRetailer.phoneNumber = authorizedRetailer?.phoneNumber;
                this.complaint.authorizedRetailer.address = authorizedRetailer?.address;
                this.complaint.authorizedRetailer.companyVatNr = authorizedRetailer?.companyVatNr;
                this.complaint.authorizedRetailer.companyVatNrEu = authorizedRetailer?.companyVatNrEu;
                this.complaint.authorizedRetailer.userId = authorizedRetailer?.userId;
                this.complaint.authorizedRetailer.companyId = authorizedRetailer?.companyId;
                this.complaint.assignedUserId = authorizedRetailer?.userId ?? null;
                this.complaint.assignedTo = 'authorizedRetailer';
                this.complaint.assignedCompanyId = authorizedRetailer?.companyId ?? null;
                this.complaint.distributor = authorizedRetailer.distributor;
                this.complaint.distributorCompanyId = authorizedRetailer.distributor?.companyId ?? null;
                this.complaint.distributorUserId = authorizedRetailer.distributor?.userId ?? null;
                this.complaint.distributorUserName = authorizedRetailer.distributor?.referenceName ?? '';
                this.complaint.distributorPhoneNumber = authorizedRetailer.distributor?.phoneNumber ?? '';
                this.complaint.distributorEmail = authorizedRetailer.distributor?.email ?? '';
            }

            this.noSalesPoint = false;
        } else {
            this.complaint.authorizedRetailerUserId = null;
            this.complaint.authorizedRetailerUserName = '';
            this.complaint.authorizedRetailerCompanyId = null;
            this.complaint.authorizedRetailerPhoneNumber = '';
            this.complaint.authorizedRetailerEmail = '';
            this.complaint.authorizedRetailer = {
                userId: null,
                companyId: null,
                referenceName: '',
                companyName: '',
                email: '',
                phoneNumber: '',
                address: '',
                companyVatNr: '',
                companyVatNrEu: '',
            };
            this.complaint.distributorUserId = null;
            this.complaint.distributorUserName = '';
            this.complaint.distributorCompanyId = null;
            this.complaint.distributorPhoneNumber = '';
            this.complaint.distributorEmail = '';
            this.complaint.distributor = {
                userId: null,
                companyId: null,
                referenceName: '',
                companyName: '',
                email: '',
                phoneNumber: '',
                address: '',
                companyVatNr: '',
                companyVatNrEu: '',
            };
            this.complaint.assignedTo = 'manufacturer';
            this.noSalesPoint = authorizedRetailer?.companyId === -1;
        }
    }

    setManufacturer() {
        this.complaint.manufacturerCompanyId = this.data.manufacturer.companyId;
        this.complaint.manufacturerUserId = this.data.manufacturer.userId;
        this.complaint.manufacturer = this.data.manufacturer;
        this.complaint.manufacturerUserName = this.data.manufacturer.referenceName;
        this.complaint.manufacturerPhoneNumber = this.data.manufacturer.phoneNumber;
        this.complaint.manufacturerEmail = this.data.manufacturer.email;
    }

    onAuthorizedRetailerSearch(event: { term: string }) {
        if (!event?.term?.length || event?.term?.length < 2) {
            this.search = '';
            this.getAuthorizedRetailers();
        } else if (this.search !== event.term && event.term.length >= 2) {
            this.search = event.term;
            this.getAuthorizedRetailers();
        }
    }

    searchAuthorizedRetailer(term: string, item: ComplaintParty): boolean {
        if (item.companyId === -1) {
            return true;
        }
        return (
            item.companyName.toLowerCase().includes(term.toLowerCase()) ||
            item.referenceName.toLowerCase().includes(term.toLowerCase()) ||
            item.companyVatNr.toLowerCase().includes(term.toLowerCase()) ||
            item.companyVatNrEu.toLowerCase().includes(term.toLowerCase()) ||
            item.address.toLowerCase().includes(term.toLowerCase())
        );
    }

    onAddressFieldChange(field: 'addressLine1' | 'city' | 'postalCode', value: any) {
        switch (field) {
            case 'addressLine1':
                this.addressLine1 = value;
                break;
            case 'city':
                this.city = value;
                break;
            case 'postalCode':
                this.postalCode = value;
                break;
        }
        this.complaint.locationPartialMatchConfirmed = false;
        this.addressValidated = false;

        this.buildFullAddress();
    }

    private buildFullAddress() {
        const parts: string[] = [];

        if (this.addressLine1?.trim()) {
            parts.push(this.addressLine1.trim());
        }

        if (this.postalCode?.trim()) {
            parts.push(this.postalCode.trim());
        }

        if (this.city?.trim()) {
            parts.push(this.city.trim());
        }

        this.complaint.installationAddress = parts.join(', ');
    }

    openMapDialog() {
        const dialogData: ComplaintMapDialogData = {
            initialPosition: this.installationAddress || undefined,
        };

        this.dialogService.open(ComplaintMapDialogComponent, { data: dialogData });
    }

    async onInstallationAddressChange() {
        this.addressValidated = false;
        this.complaint.locationSource = 'address';
        this.complaint.latitude = undefined;
        this.complaint.longitude = undefined;
        await this.geocodeAddressWithPlacesAPI(this.complaint.installationAddress);
    }

    onAddressModeChange(mode: 'exact' | 'map') {
        this.addressMode = mode;

        if (mode === 'map') {
            this.complaintFormValidatedFields.get('addressLine1')?.clearValidators();
            this.complaintFormValidatedFields.get('addressLine1')?.updateValueAndValidity();
            this.complaint.locationPartialMatchConfirmed = false;

            if (!this.installationAddress) {
                this.installationAddress = {
                    lat: 52.0,
                    lng: 19.0
                };
                this.complaint.latitude = 52.0;
                this.complaint.longitude = 19.0;
                this.mapZoom = 6;
            }
        } else {
            this.addressLine1 = this.complaintFormValidatedFields.get('addressLine1')?.value;
            this.complaintFormValidatedFields.get('addressLine1')?.setValidators([Validators.required]);
            this.complaintFormValidatedFields.get('addressLine1')?.updateValueAndValidity();
            this.mapZoom = 15;
        }
        this.buildFullAddress();
    }

    async onMarkerDragEnd(event: google.maps.MapMouseEvent) {
        if (event.latLng) {
            this.installationAddress = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng()
            };
            this.complaint.locationSource = 'manual';
            this.complaint.latitude = event.latLng.lat();
            this.complaint.longitude = event.latLng.lng();
            this.complaint.isLocationPartialMatch = false;
            this.complaint.locationPartialMatchConfirmed = false;
            this.addressValidated = true;
            this.getAuthorizedRetailers();
        }
    }

    private handleMapLoadError(errorType: string) {
        console.error(`Map load error: ${errorType}`);
        this.mapLoadError = true;
        this.complaint.mapUnavailable = true;
        this.apiLoaded = false;
        this.chRef.detectChanges();
    }

    reloadMap() {
        this.mapLoadError = false;
        this.apiLoaded = false;
        this.chRef.detectChanges();

        const loader = new Loader({
            apiKey: this.data.googleMapsApiKey,
            version: 'weekly',
            libraries: ['places'],
        });

        (window as any).gm_authFailure = () => {
            console.error('Google Maps API authentication failed');
            this.handleMapLoadError('AUTH_FAILURE');
        };

        const loadTimeout = setTimeout(() => {
            if (!this.apiLoaded) {
                console.error('Google Maps API reload timeout');
                this.handleMapLoadError('TIMEOUT');
            }
        }, 10000);

        loader.load().then(() => {
            clearTimeout(loadTimeout);
            this.apiLoaded = true;
            this.autocompleteService = new google.maps.places.AutocompleteService();
            this.chRef.detectChanges();
        }).catch((error) => {
            clearTimeout(loadTimeout);
            console.error('Google Maps API reload error:', error);
            if (error.message?.includes('quota') || error.message?.includes('OVER_QUERY_LIMIT')) {
                this.handleMapLoadError('QUOTA_EXCEEDED');
            } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
                this.handleMapLoadError('NETWORK_ERROR');
            } else {
                this.handleMapLoadError('UNKNOWN_ERROR');
            }
        });
    }

    private async geocodeAddressWithPlacesAPI(address: string): Promise<void> {
        if (!address || !this.autocompleteService) {
            return;
        }

        try {
            const placeId = await this.getPlaceIdFromAutocomplete(address);

            if (placeId) {
                const success = await this.geocodeByPlaceId(placeId);

                if (success) {
                    return;
                }
            }

            await this.directGeocode(address);

        } catch (error) {
            await this.directGeocode(address);
        }
    }

    private async getPlaceIdFromAutocomplete(address: string): Promise<string | null> {
        if (!this.autocompleteService) {
            return null;
        }

        return new Promise((resolve) => {
            const hasPolishPostalCode = /\b\d{2}-\d{3}\b/.test(address);

            let searchAddress = address;
            let types = ['address'];
            if(this.addressMode === 'map') {
                types = []
                if (hasPolishPostalCode && !address.toLowerCase().includes('polska') && !address.toLowerCase().includes('poland')) {
                    searchAddress = `${address}, Polska`;
                }
            }

            const request: google.maps.places.AutocompletionRequest = {
                input: searchAddress,
                types
            };

            if (hasPolishPostalCode) {
                request.componentRestrictions = { country: 'pl' };
            }

            this.autocompleteService!.getPlacePredictions(request, (predictions, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
                    resolve(predictions[0].place_id);
                } else {
                    resolve(null);
                }
            });
        });
    }

    private checkAddressMatch(result: google.maps.GeocoderResult): boolean {
        let geocodedCity = '';
        let geocodedPostalCode = '';

        for (const component of result.address_components) {
            if (component.types.includes('locality')) {
                geocodedCity = component.long_name;
            }
            if (component.types.includes('postal_code')) {
                geocodedPostalCode = component.long_name;
            }
        }

        const normalizeString = (str: string) => str.trim().toLowerCase().replace(/\s+/g, '');

        const userCity = normalizeString(this.city);
        const userPostalCode = normalizeString(this.postalCode);
        const resultCity = normalizeString(geocodedCity);
        const resultPostalCode = normalizeString(geocodedPostalCode);

        const cityMismatch = userCity !== '' && resultCity !== '' && userCity !== resultCity;
        const postalCodeMismatch = userPostalCode !== '' && resultPostalCode !== '' && userPostalCode !== resultPostalCode;

        return cityMismatch || postalCodeMismatch;
    }

    private async geocodeByPlaceId(placeId: string): Promise<boolean> {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                console.error('Geocode by place ID timeout');
                this.addressValidated = false;
                resolve(false);
            }, 5000);

            this.geocoder
                .geocode({
                    placeId: placeId,
                })
                .subscribe(
                    ({ results, status }) => {
                        clearTimeout(timeout);

                        if (status === 'OVER_QUERY_LIMIT') {
                            console.error('Geocoding quota exceeded');
                            this.addressValidated = false;
                            resolve(false);
                            return;
                        }

                        if (results && results.length > 0) {
                            this.installationAddress = {
                                lat: results[0].geometry.location.lat(),
                                lng: results[0].geometry.location.lng(),
                            };
                            this.complaint.latitude = results[0].geometry.location.lat();
                            this.complaint.longitude = results[0].geometry.location.lng();

                            const hasPartialMatchFlag = results[0].partial_match === true;
                            const isLowPrecision = results[0].geometry.location_type !== 'ROOFTOP';
                            const hasAddressMismatch = this.checkAddressMatch(results[0]);

                            this.complaint.isLocationPartialMatch = hasPartialMatchFlag || isLowPrecision || hasAddressMismatch;

                            this.getAuthorizedRetailers();
                            this.addressValidated = true;
                            resolve(true);
                        } else {
                             this.installationAddress = {
                                lat: 52.0,
                                lng: 19.0
                            };
                            this.complaint.latitude = 52.0;
                            this.complaint.longitude = 19.0;
                            this.mapZoom = 6;
                            this.addressValidated = false;
                            resolve(true);
                        }
                    },
                    (error) => {
                        clearTimeout(timeout);
                        console.error('Geocode by place ID error:', error);
                        this.addressValidated = false;
                        resolve(false);
                    }
                );
        });
    }

    private async directGeocode(address: string): Promise<boolean> {
        return new Promise((resolve) => {
            const hasPolishPostalCode = /\b\d{2}-\d{3}\b/.test(address);

            const geocodeRequest: google.maps.GeocoderRequest = {
                address: address,
            };

            if (hasPolishPostalCode) {
                geocodeRequest.componentRestrictions = { country: 'pl' };
            }

            const timeout = setTimeout(() => {
                console.error('Direct geocode timeout');
                this.addressValidated = false;
                resolve(false);
            }, 5000);

            this.geocoder
                .geocode(geocodeRequest)
                .subscribe(
                    ({ results, status }) => {
                        clearTimeout(timeout);

                        if (status === 'OVER_QUERY_LIMIT') {
                            console.error('Geocoding quota exceeded');
                            this.addressValidated = false;
                            resolve(false);
                            return;
                        }

                        if (results && results.length > 0) {
                            this.installationAddress = {
                                lat: results[0].geometry.location.lat(),
                                lng: results[0].geometry.location.lng(),
                            };
                            this.complaint.latitude = results[0].geometry.location.lat();
                            this.complaint.longitude = results[0].geometry.location.lng();

                            const hasPartialMatchFlag = results[0].partial_match === true;
                            const isLowPrecision = results[0].geometry.location_type !== 'ROOFTOP';
                            const hasAddressMismatch = this.checkAddressMatch(results[0]);

                            this.complaint.isLocationPartialMatch = hasPartialMatchFlag || isLowPrecision || hasAddressMismatch;

                            this.getAuthorizedRetailers();
                            this.addressValidated = true;
                            resolve(true);
                        } else {
                             this.installationAddress = {
                                lat: 52.0,
                                lng: 19.0
                            };
                            this.complaint.latitude = 52.0;
                            this.complaint.longitude = 19.0;
                            this.mapZoom = 6;
                            this.addressValidated = false;
                            resolve(false);
                        }
                    },
                    (error) => {
                        clearTimeout(timeout);
                        console.error('Direct geocode error:', error);
                        this.addressValidated = false;
                        resolve(false);
                    }
                );
        });
    }


    async getAuthorizedRetailers() {
        const authorizedRetailersFromServer =
            (await this.http
                .post<AuthorizedRetailer[]>(
                    `${window.location.origin}/iccadmin/${
                        this.configService?.user?.user?.ActivePanel === 'complaintb2c'
                            ? 'complaintb2c'
                            : 'trade'
                        }/user/contractors/get-authorized-retailers`,
                    {
                        lat: this.installationAddress?.lat,
                        lng: this.installationAddress?.lng,
                        search: this.search,
                        orderAuthorizedRetailerExtId: this.router.parseUrl(this.router.url).queryParams['authorizedRetailerId'],
                    }
                )
                .toPromise()) ?? [];

        this.authorizedRetailers = [this.noAuthorizedRetailerOption, ...authorizedRetailersFromServer];
    }

    showPurchaseDateInfo() {
        this.complaintService.openGlobalInfo('complaint-purchase-date-instruction');
    }

    focusFirstInvalidField() {
        const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector('input.ng-invalid, textarea.ng-invalid, select.ng-invalid');
        if (firstInvalidControl) {
            firstInvalidControl.focus();
        }
    }

    async save() {
        this.el.nativeElement.querySelectorAll('input.ng-invalid, textarea.ng-invalid, select.ng-invalid').forEach((el: HTMLElement) => {
            el.classList.add('ng-touched');
        });
        let validForm = false;

        if (this.isB2bComplaintForm) {
            validForm = !!(
                this.complaint.documentTypeId !== ComplaintsDocumentType.Complaint || this.complaint.purchaseDate) &&
                this.complaintPositions.length > 0 &&
                this.complaintPositions.every(
                    (el) =>
                        el.complaintProductTypeId &&
                        el.complaintDocumentType &&
                        (this.complaint.documentTypeId !== ComplaintsDocumentType.Complaint || el.defectDate) &&
                        el.description &&
                        this.complaintPositionsAttachments.length > 0 &&
                        this.complaintPositionsAttachments.some(
                            (attachment) =>
                                attachment.documentPositionId === el.uuid &&
                                attachment.attachmentType === 'closeUpPhoto'
                        ) &&
                        this.complaintPositionsAttachments.some(
                            (attachment) =>
                                attachment.documentPositionId === el.uuid &&
                                attachment.attachmentType === 'wholeProductPhoto'
                        )
                );
        } else {
            const addressValid = this.addressMode === 'exact'
                ? !!(this.complaint.installationAddress && (this.addressValidated || this.complaint.locationSource === 'manual'))
                : !!(this.complaint.locationDescription || this.installationAddress);

            validForm = !!(
                this.complaintFormValidatedFields.valid &&
                this.complaint.clientName &&
                this.complaint.clientEmail &&
                this.complaint.clientPhoneNumber &&
                addressValid &&
                (this.complaint.documentTypeId !== ComplaintsDocumentType.Complaint || this.complaint.purchaseDate) &&
                this.complaint.consents.every((el) => !el.required || el.checked) &&
                this.complaintPositions.length > 0 &&
                this.complaintPositions.every(
                    (el) =>
                        el.complaintProductTypeId &&
                        el.complaintDocumentType &&
                        (this.complaint.documentTypeId !== ComplaintsDocumentType.Complaint || el.defectDate) &&
                        el.description &&
                        this.complaintPositionsAttachments.length > 0 &&
                        this.complaintPositionsAttachments.some(
                            (attachment) =>
                                attachment.documentPositionId === el.uuid &&
                                attachment.attachmentType === 'closeUpPhoto'
                        ) &&
                        this.complaintPositionsAttachments.some(
                            (attachment) =>
                                attachment.documentPositionId === el.uuid &&
                                attachment.attachmentType === 'wholeProductPhoto'
                        )
                )
            );
        }

        if (validForm) {
            this.complaint.clientPhoneNumber = this.complaint.clientPhoneNumber.replaceAll(' ', '');

            if (this.addressMode === 'map') {
                this.addressLine1 = '';
                this.complaint.addressLine_1 = '';
                this.complaintFormValidatedFields.get('addressLine1')?.setValue('', {
                    emitEvent: false,
                });
            } else {
                this.complaint.addressLine_1 = this.addressLine1;
                this.complaint.locationDescription = '';
            }
            this.complaint.postalCode = this.postalCode;
            this.complaint.city = this.city;
            this.buildFullAddress();

            this.loaderService.setLoading();
            this.complaintPositions.forEach((el) => {
                this.complaintService.clearValuesByDocumentType(el);
            });
            this.saving = true;
            const result = await this.complaintService.saveB2cComplaint(
                this.complaint,
                this.complaintPositions,
                this.complaintPositionsAttachments
            );
            this.documentNumber = this.complaint.documentNumber?.join('') ?? '';

            if (result) {
                this.loaderService.setLoaded();
                this.saving = false;
                this.showSyncedInfo = true;
                this.chRef.detectChanges();
            } else {
                this.loaderService.setLoaded();
                this.saving = false;
                this.toastService.add({
                    severity: 'error',
                    title: this.translateService.instant('COMPLAINT|Błąd zapisu'),
                    body: this.translateService.instant(
                        'COMPLAINT|Nie udało się zapisać reklamacji. Spróbuj ponownie.'
                    ),
                });
            }
        } else if (!this.complaintPositions.length) {
            this.toastService.add({
                severity: 'error',
                title: this.translateService.instant('COMPLAINT|Błąd zapisu'),
                body: this.translateService.instant('COMPLAINT|Dodaj przynajmniej jedną usterkę do reklamacji.'),
            });
        } else {
            this.formSubmitted = true;
            this.focusFirstInvalidField();
            this.toastService.add({
                severity: 'error',
                title: this.translateService.instant('COMPLAINT|Błąd zapisu'),
                body: this.translateService.instant('COMPLAINT|Wypełnij wszystkie wymagane pola'),
            });
        }
    }

    getTrustedHtml(html: string): SafeHtml {
        if (!this.trustedHtmlCache.has(html)) {
            this.trustedHtmlCache.set(html, this.sanitizer.bypassSecurityTrustHtml(html));
        }
        return this.trustedHtmlCache.get(html)!;
    }

    checkDocumentType(documentTypeId: string) {
        return Number(this.complaint.documentTypeId) === Number(documentTypeId);
    }

    getAvailableDocumentTypes(): Record<number, string> {
        const availableTypes: Record<number, string> = {};
        const workflows = this.complaintService.workflows || [];

        for (const [typeId, typeName] of Object.entries(this.documentTypesDict)) {
            const hasActiveWorkflow = workflows.some(
                (workflow) => workflow.documentTypeId === +typeId && workflow.active === true
            );
            if (hasActiveWorkflow) {
                availableTypes[+typeId] = typeName;
            }
        }

        return availableTypes;
    }

    onNoSalesPointSelect() {
        this.noSalesPoint = !this.noSalesPoint;
    }

    getAttachmentsByPositionId(positionId: string) {
        return this.complaintPositionsAttachments.filter(
            (el) => el.documentPositionId === positionId
        );
    }

    previousStep() {
        this.currentStep--;
        setTimeout(() => {
            const activeStep = document.getElementById(`step-${this.currentStep - 1}`);
            activeStep?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
        });
    }

    async nextStep() {
        this.el.nativeElement.querySelectorAll('input.ng-invalid, textarea.ng-invalid, select.ng-invalid').forEach((el: HTMLElement) => {
            el.classList.add('ng-touched');
        });

        if (this.currentStep === 1 && !this.isB2bComplaintForm && this.complaint.installationAddress && !this.addressValidated) {
            await this.onInstallationAddressChange();
        }

        if (this.currentStep === 3) {
            const positionsTitles = new Map<string, string[]>();
            const positionsAttachmentsMap = new Map<string, DocumentAttachment[]>();
            this.complaintPositions.forEach((el) => {
                positionsTitles.set(el.uuid, this.complaintService.getPositionTitle(el, false));
                positionsAttachmentsMap.set(el.uuid, this.getAttachmentsByPositionId(el.uuid));
            });
            this.positionsTitles = positionsTitles;
            this.positionsAttachmentsMap = positionsAttachmentsMap;
        }

        if (this.currentStep === 1 && !this.isB2bComplaintForm &&
            this.addressMode === 'exact' &&
            this.city.length > 0 &&
            this.postalCode.length > 0 &&
            this.addressLine1.length > 0 &&
            this.complaint.isLocationPartialMatch && !this.complaint.locationPartialMatchConfirmed
        ) {
            const dialogRef = this.dialogService.open(ComplaintPartialMatchConfirmDialogComponent, {});
            const confirmed = await dialogRef.afterClosed().toPromise();
            if (!confirmed) {
                return;
            }

            this.complaint.locationPartialMatchConfirmed = true;
        }

        if (this.allStepFieldsValid(this.currentStep)) {
            this.currentStep++;
            setTimeout(() => {
                const activeStep = document.getElementById(`step-${this.currentStep - 1}`);
                activeStep?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
            });
        } else {
            this.focusFirstInvalidField();

            if (this.currentStep === 1 && !this.isB2bComplaintForm &&
                this.complaint.installationAddress && !this.addressValidated) {
                this.toastService.add({
                    severity: 'error',
                    title: this.translateService.instant('COMPLAINT|Błąd adresu'),
                    body: this.translateService.instant('COMPLAINT|Wprowadzony adres nie został rozpoznany. Popraw adres i spróbuj ponownie.'),
                });
            } else {
                this.toastService.add({
                    severity: 'error',
                    title: this.translateService.instant('INTERFACE|Błąd'),
                    body: this.translateService.instant('COMPLAINT|Wypełnij wszystkie wymagane pola, aby przejść do następnego kroku'),
                });
            }
        }
    }

    allStepFieldsValid(step: number) {
        if (step === 1) {
            return (this.complaintFormValidatedFields.valid &&
                this.complaint.clientName &&
                this.complaint.clientEmail &&
                this.complaint.clientPhoneNumber &&
                this.complaint.installationAddress &&
                this.addressValidated);
        }
        if (step === 2) {
            return (this.complaint.documentTypeId !== ComplaintsDocumentType.Complaint || this.complaint.purchaseDate);
        }
        if (step === 3) {
            return this.complaintPositions.length > 0 && this.requiredPositionsDataProvided();
        }

        return false;
    }

    requiredPositionsDataProvided() {
        return this.complaintPositions.length > 0 && this.complaintPositions.every(
            (el) =>
                el.complaintProductTypeId &&
                el.complaintDocumentType &&
                (this.complaint.documentTypeId !== ComplaintsDocumentType.Complaint || el.defectDate) &&
                el.description &&
                (!el.invalidSerialNumber || el.complaintDocumentType !== 'serialNumber') &&
                this.complaintPositionsAttachments.length > 0 &&
                this.complaintPositionsAttachments.some(
                    (attachment) =>
                        attachment.documentPositionId === el.uuid &&
                        attachment.attachmentType === 'closeUpPhoto'
                ) &&
                this.complaintPositionsAttachments.some(
                    (attachment) =>
                        attachment.documentPositionId === el.uuid &&
                        attachment.attachmentType === 'wholeProductPhoto'
                )
        );
    }

    toggleAllConsents(checked: boolean) {
        this.allConsentsChecked = checked;
        this.complaint.consents.forEach((el) => {
            el.checked = checked;
        });
    }

    getPositionTitle(position: ComplaintPosition) {
        return this.complaintService.getPositionTitle(position, false);
    }

    setCurrentUserAsReporter() {
        if (this.activeUserType === 'authorizedRetailer') {
            this.setCurrentAuthorizedRetailerAsReporter();
        } else if (this.activeUserType === 'distributor') {
            this.setCurrentDistributorAsReporter();
        }
    }

    setCurrentAuthorizedRetailerAsReporter() {
        const currentUser = this.configService.user;

        const distributorData = this.complaintService.contractors[this.data.manufacturer.companyId].find((el) => el.userType === 'distributor');

        const distributor: ComplaintParty = {
            userId: distributorData?.userId,
            companyId: +currentUser?.parentCompany?.id,
            referenceName: distributorData?.referenceName,
            companyName: currentUser?.parentCompany?.name,
            address: currentUser?.parentCompany?.addresses[0].addressLine1 ?? '' + ' ' + currentUser?.parentCompany?.addresses[0].addressLine2 ?? '' + ' ' + currentUser?.parentCompany?.addresses[0].postalCode ?? '' + ' ' + currentUser?.parentCompany?.addresses[0].city ?? '',
            email: currentUser?.parentCompany?.emails[0]?.email ?? '',
            phoneNumber: currentUser?.parentCompany?.phoneNumbers[0]?.phoneNumber ?? '',
            companyVatNr: currentUser?.parentCompany?.vatNr,
            companyVatNrEu: currentUser?.parentCompany?.vatNrEu,
        };

        this.complaint.authorizedRetailerUserId = currentUser?.id;
        this.complaint.authorizedRetailerUserName = currentUser?.user?.person?.reference_name;
        this.complaint.authorizedRetailerCompanyId = +currentUser?.user?.ActiveCompany;
        this.complaint.authorizedRetailerPhoneNumber = currentUser?.phoneNumber ?? '';
        this.complaint.authorizedRetailerEmail = currentUser?.user?.email ?? '';
        this.complaint.authorizedRetailer.referenceName = currentUser?.user?.person?.reference_name;
        this.complaint.authorizedRetailer.companyName = currentUser?.company?.name;
        this.complaint.authorizedRetailer.email = currentUser?.user?.email ?? '';
        this.complaint.authorizedRetailer.phoneNumber = currentUser?.phoneNumber ?? '';
        this.complaint.authorizedRetailer.address = currentUser?.company?.addresses[0].addressLine1 ?? '' + ' ' + currentUser?.company?.addresses[0].addressLine2 ?? '' + ' ' + currentUser?.company?.addresses[0].postalCode ?? '' + ' ' + currentUser?.company?.addresses[0].city ?? '';
        this.complaint.authorizedRetailer.companyVatNr = currentUser?.company?.vatNr;
        this.complaint.authorizedRetailer.companyVatNrEu = currentUser?.company?.vatNrEu;
        this.complaint.authorizedRetailer.userId = currentUser?.id;
        this.complaint.authorizedRetailer.companyId = +currentUser?.user?.ActiveCompany;
        this.complaint.assignedUserId = distributorData?.userId;
        this.complaint.assignedTo = 'distributor';
        this.complaint.assignedCompanyId = +currentUser?.parentCompany?.id;
        this.complaint.distributor = distributor;
        this.complaint.distributorCompanyId = +currentUser?.parentCompany?.id;
        this.complaint.distributorUserId = distributorData?.userId;
        this.complaint.distributorUserName = distributorData?.referenceName;
        this.complaint.distributorPhoneNumber = currentUser?.parentCompany?.phoneNumbers[0]?.phoneNumber ?? '';
        this.complaint.distributorEmail = currentUser?.parentCompany?.emails[0]?.email ?? '';
    }

    setCurrentDistributorAsReporter() {
        const currentUser = this.configService.user;

        this.complaint.authorizedRetailerUserId = null;
        this.complaint.authorizedRetailerUserName = '';
        this.complaint.authorizedRetailerCompanyId = null;
        this.complaint.authorizedRetailerPhoneNumber = '';
        this.complaint.authorizedRetailerEmail = '';
        this.complaint.authorizedRetailer = {
            userId: null,
            companyId: null,
            referenceName: '',
            companyName: '',
            email: '',
            phoneNumber: '',
            address: '',
            companyVatNr: '',
            companyVatNrEu: '',
        };
        this.complaint.distributorUserId = currentUser?.id;
        this.complaint.distributorUserName = currentUser?.user?.person?.reference_name;
        this.complaint.distributorCompanyId = +currentUser?.user?.ActiveCompany;
        this.complaint.distributorPhoneNumber = currentUser?.phoneNumber ?? '';
        this.complaint.distributorEmail = currentUser?.user?.email ?? '';
        this.complaint.distributor = {
            userId: null,
            companyId: null,
            referenceName: currentUser?.user?.person?.reference_name,
            companyName: currentUser?.company?.name,
            email: currentUser?.user?.email ?? '',
            phoneNumber: currentUser?.phoneNumber ?? '',
            address: currentUser?.company?.addresses[0].addressLine1 ?? '' + ' ' + currentUser?.company?.addresses[0].addressLine2 ?? '' + ' ' + currentUser?.company?.addresses[0].postalCode ?? '' + ' ' + currentUser?.company?.addresses[0].city ?? '',
            companyVatNr: currentUser?.company?.vatNr,
            companyVatNrEu: currentUser?.company?.vatNrEu,
        };
        this.complaint.assignedUserId = null;
        this.complaint.assignedTo = 'manufacturer';
        this.complaint.assignedCompanyId = this.data.manufacturer.companyId;
    }
}
